import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Edit, Trash2, GripVertical, ExternalLink, Eye, EyeOff, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

const AdminRoutesListPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', url: '', is_active: true });
  const [draggedIdx, setDraggedIdx] = useState(null);

  const getToken = () => localStorage.getItem('vipgez_admin_token');

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/routes-list`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (data.success) setItems(data.items || []);
    } catch (e) {
      console.error('Failed to fetch routes list:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!getToken()) { navigate('/login-goturkeytransfer'); return; }
    fetchItems();
  }, [navigate, fetchItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();
    const isEdit = !!editingItem;
    const url = isEdit
      ? `${API_BASE}/api/admin/routes-list/${editingItem.id}`
      : `${API_BASE}/api/admin/routes-list`;

    try {
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(isEdit ? 'Route link updated' : 'Route link created');
        setShowForm(false);
        setEditingItem(null);
        setFormData({ name: '', url: '', is_active: true });
        fetchItems();
      } else {
        toast.error(data.detail || 'Operation failed');
      }
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this route link?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/admin/routes-list/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (data.success) { toast.success('Deleted'); fetchItems(); }
      else toast.error('Delete failed');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const toggleActive = async (item) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/routes-list/${item.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !item.is_active }),
      });
      const data = await res.json();
      if (data.success) fetchItems();
    } catch (e) {
      toast.error('Toggle failed');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name || '', url: item.url || '', is_active: item.is_active ?? true });
    setShowForm(true);
  };

  // Drag & drop reorder
  const handleDragStart = (idx) => setDraggedIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = async (targetIdx) => {
    if (draggedIdx === null || draggedIdx === targetIdx) return;
    const updated = [...items];
    const [moved] = updated.splice(draggedIdx, 1);
    updated.splice(targetIdx, 0, moved);
    setItems(updated);
    setDraggedIdx(null);

    // Save new order
    const order = updated.map((item) => item.id);
    try {
      await fetch(`${API_BASE}/api/admin/routes-list-reorder`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ order }),
      });
      toast.success('Order saved');
    } catch (e) {
      toast.error('Reorder failed');
    }
  };

  return (
    <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="routes-list-title">Routes List</h1>
              <p className="text-muted-foreground text-sm">Manage quick-link routes shown on homepage</p>
            </div>
            <Button
              onClick={() => { setShowForm(true); setEditingItem(null); setFormData({ name: '', url: '', is_active: true }); }}
              data-testid="add-route-link-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Link
            </Button>
          </div>

          {/* Form */}
          {showForm && (
            <Card className="mb-6 bg-secondary/50 border-white/10" data-testid="route-link-form">
              <CardHeader className="border-b border-white/10 py-4">
                <CardTitle className="text-white text-base">{editingItem ? 'Edit Link' : 'New Link'}</CardTitle>
              </CardHeader>
              <CardContent className="pt-5">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">Display Name</label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                        placeholder="e.g., Istanbul Airport to Taksim"
                        required
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                        data-testid="route-link-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">URL</label>
                      <Input
                        value={formData.url}
                        onChange={(e) => setFormData((p) => ({ ...p, url: e.target.value }))}
                        placeholder="/istanbul-airport-transfer/"
                        required
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                        data-testid="route-link-url-input"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.checked }))}
                      className="rounded border-white/20 bg-black/30 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-300">Active</span>
                  </label>
                  <div className="flex gap-3 pt-1">
                    <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="route-link-save-btn">
                      {editingItem ? 'Update' : 'Create'}
                    </Button>
                    <Button type="button" variant="outline" className="border-white/20 text-gray-300 hover:bg-white/10"
                      onClick={() => { setShowForm(false); setEditingItem(null); }}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* List */}
          <Card className="bg-secondary border-border">
            <CardHeader className="py-4">
              <CardTitle className="text-foreground text-base">Links ({items.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
                </div>
              ) : items.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground text-sm">
                  No route links yet. Click &quot;Add Link&quot; to create one.
                </div>
              ) : (
                <div className="divide-y divide-border/50" data-testid="routes-list-items">
                  {items.map((item, idx) => (
                    <div
                      key={item.id || idx}
                      className="flex items-center gap-3 px-5 py-3 hover:bg-white/[0.03] transition-colors"
                      draggable
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(idx)}
                      data-testid={`routes-list-item-${idx}`}
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground/50 cursor-grab flex-shrink-0" />

                      <span className="text-xs text-muted-foreground/60 w-6 text-center flex-shrink-0">
                        {idx + 1}
                      </span>

                      <span className="flex-1 text-sm font-medium text-foreground truncate">
                        {item.name}
                      </span>

                      <span className="hidden sm:block text-xs text-muted-foreground truncate max-w-[180px]">
                        {item.url}
                      </span>

                      <button
                        onClick={() => toggleActive(item)}
                        className={`p-1.5 rounded transition-colors ${item.is_active ? 'text-green-400 hover:bg-green-500/10' : 'text-gray-500 hover:bg-gray-500/10'}`}
                        title={item.is_active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                        data-testid={`toggle-active-${idx}`}
                      >
                        {item.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>

                      <button onClick={() => handleEdit(item)} className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground/60 mt-3 text-center">
            Drag items to reorder. Only active links appear on the homepage.
          </p>
        </div>
      </main>
    </div>
  );
};

export default AdminRoutesListPage;
